import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  ButtonSelect,
  Icon,
  InputField,
  MenuMulti,
  type MenuMultiOption,
} from '@saviynt/design-system';
import cx from 'classnames';

import { DEFAULT_SEARCH_DEBOUNCE } from '../../../constants';
import {
  ACCESS_TYPE_FILTER_VALUE,
  ENTITLEMENT_TYPE_FILTER_VALUE,
} from '../constants';

import './ListControls.css';

export interface ListFilters {
  label: string;
  value: string;
  options: MenuMultiOption[];
}

interface ListControlsProps {
  isDisabled?: boolean;
  controls?: ListFilters[];
  setSearchText: (text: string) => void;
  handleControls?: (filters: ListFilters[]) => void;
}

/**
 * ListControls
 */
function ListControls({
  isDisabled,
  controls,
  setSearchText,
  handleControls,
}: ListControlsProps) {
  const [text, setText] = useState('');
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  // constant
  const isBadge = useMemo(
    () => controls?.some((filter) => filter.options.some((o) => o.isChecked)),
    [controls]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(text);
    }, DEFAULT_SEARCH_DEBOUNCE);

    return () => clearTimeout(timer);
  }, [text, setSearchText]);

  useEffect(() => {
    if (!isDisabled) {
      setIsFilterBarOpen(false);
    }
  }, [isDisabled]);

  const handleChange = useCallback(
    (value: string, selectedOptions: MenuMultiOption[] | undefined) => {
      if (controls && handleControls) {
        const modifiedControls = controls.map((f) => {
          if (
            f.value === value &&
            (value === ACCESS_TYPE_FILTER_VALUE ||
              value === ENTITLEMENT_TYPE_FILTER_VALUE)
          ) {
            const currentValidOptions = f.options
              .filter((o) => !o.isChecked)
              .map((o) => o.value);

            return {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: selectedOptions?.some(
                  (so) =>
                    so.value === o.value &&
                    currentValidOptions.includes(o.value)
                ),
              })),
            };
          }

          if (f.value === value) {
            return {
              ...f,
              options: f.options.map((o) => ({
                ...o,
                isChecked: selectedOptions?.some((so) => so.value === o.value),
              })),
            };
          }

          return f;
        });

        handleControls(modifiedControls);
      }
    },
    [controls, handleControls]
  );

  return (
    <Box tag='div' className='ListControls'>
      <Box tag='div' className='ListControls-wrapper'>
        <InputField
          name='basic'
          placeholder='Search by name and description'
          kind='search'
          size='medium'
          prefixIcon={
            <Icon kind='Search' color='--color-foreground-neutral-subtle' />
          }
          value={text}
          setValue={setText}
          isDisabled={isDisabled}
        />
        {controls && (
          <ButtonSelect
            kind='icon'
            ariaLabel='Filter'
            onClick={() => setIsFilterBarOpen((prev) => !prev)}
            isOpen={isFilterBarOpen}
            size='medium'
            prefixIcon={<Icon kind='Filter' />}
            isDisabled={isDisabled}
            BadgeComp={
              isBadge ? <Badge kind='dot' color='primary' size='small' /> : null
            }
          />
        )}
      </Box>
      {controls && isFilterBarOpen && (
        <Box
          tag='div'
          className={cx(
            'ListControls-filterBar',
            controls.length > 0 && 'ListControls-filterBar--isAvailable'
          )}>
          {controls.map((filter) => (
            <MenuMulti
              key={filter.value}
              options={filter.options}
              label={filter.label}
              onChange={(selectedOptions) => {
                handleChange(filter.value, selectedOptions);
              }}
              trigger={
                <ButtonSelect kind='pill' label='' isDisabled={isDisabled} />
              }
              minOptionsForFooter={2}
              minOptionsForSearch={filter.options.length + 1}
              footerLink1Text=' '
              footerLink2Text='Clear Selection'
              onFooterLink2Click={() => handleChange(filter.value, undefined)}
              sortOnCheck={false}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

ListControls.defaultProps = {
  isDisabled: false,
  controls: null,
  handleControls: () => {},
};

export default memo(ListControls);
